import { supabase } from './supabase';

/**
 * Client voor POST /api/orders/b2b-cyclus-afronden — het afrond-primitief voor
 * B2B-cyclusklanten (factuur aanmaken + versturen + WO Afgerond + registry).
 * Endpoint is idempotent; her-aanroepen na een halve mislukking is de retry.
 */

export interface B2BCyclusAfrondenResult {
  ok: boolean;
  invoiceId: string;
  invoiceNaam: string | null;
  steps: {
    invoice?: 'created' | 'existing';
    sent?: 'sent' | 'skipped-already-sent';
    status?: 'afgerond' | 'was-al-afgerond';
    registry?: 'bijgewerkt' | 'geen-registry-rij';
  };
}

/**
 * Eindstatussen waarbij afronden niet (meer) aan de orde is. Alles daarbuiten
 * ("Wordt gebracht", "Zelf gebracht", wasserij-statussen, "Klaargelegd", …) is
 * een openstaande cyclus-order: zodra de klant hem fysiek ophaalt, mag hij
 * afgerond + gefactureerd worden. Het endpoint guard't zelf alleen op
 * Geannuleerd/Terugbetaald; dit filter is voor de UI.
 */
export const B2B_CYCLUS_EINDSTATUSSEN = ['Afgerond', 'Geannuleerd', 'Terugbetaald'];

export function isB2BCyclusOpenStatus(status: string): boolean {
  return !B2B_CYCLUS_EINDSTATUSSEN.includes(status);
}

export class B2BCyclusError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export async function afrondenEnFactureren(
  werkOrderId: string,
  opts?: { force?: boolean },
): Promise<B2BCyclusAfrondenResult> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new B2BCyclusError('AUTH', 'Niet ingelogd');

  let res: Response;
  try {
    res = await fetch('/api/orders/b2b-cyclus-afronden', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ werkOrderId, force: opts?.force === true }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new B2BCyclusError('NETWORK', msg);
  }

  let payload: Partial<B2BCyclusAfrondenResult> & { error?: string; code?: string };
  try {
    payload = await res.json();
  } catch {
    throw new B2BCyclusError('PARSE', `Server respons ongeldig (status ${res.status})`);
  }

  if (!res.ok || !payload.ok) {
    throw new B2BCyclusError(payload.code || 'UNKNOWN', payload.error || 'Afronden mislukt');
  }
  return {
    ok: true,
    invoiceId: payload.invoiceId!,
    invoiceNaam: payload.invoiceNaam ?? null,
    steps: payload.steps ?? {},
  };
}
